<template>
  <div
    ref="QRElementRef"
    spellcheck="false"
    class="group relative m-2 h-[600px] w-[26rem] overflow-hidden border bg-white"
  >
    <div class="absolute -left-64 top-[-27.5rem] h-1/2 w-full rotate-[135deg] scale-[200%] transform bg-[#7B7B7B]" />

    <div class="absolute top-[-10rem] h-1 w-full rotate-[135deg] scale-[200%] transform bg-[#424242]" />

    <div class="absolute top-[-6.5rem] h-1 w-full rotate-[135deg] scale-[200%] transform bg-[#424242]" />

    <div class="absolute top-[52%] h-1 w-32 rotate-[135deg] scale-[200%] transform bg-[#424242]" />

    <div class="absolute right-0 top-[30%] h-5 w-32 rotate-[135deg] scale-[200%] transform bg-[#424242]" />

    <div class="absolute right-0 top-[45%] h-1 w-32 rotate-[135deg] scale-[200%] transform bg-[#7B7B7B]" />

    <div class="absolute -right-56 bottom-[-27rem] h-1/2 w-full rotate-[135deg] scale-[200%] transform bg-[#7B7B7B]" />

    <div class="absolute bottom-[-9.5rem] h-1.5 w-full rotate-[135deg] scale-[200%] transform bg-[#424242]" />

    <div
      class="absolute bottom-[-27rem] left-[-17rem] h-1/2 w-full rotate-[60deg] scale-[200%] transform bg-[#7B7B7B]"
    />

    <div class="relative mx-5">
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTitle')"
        class="text-neutral-5 group-hover:border-neutral-40 group-hover:focus:border-primary-40 mx-10 flex h-28 flex-col justify-center overflow-hidden break-words rounded border-2 border-transparent text-center focus:outline-none group-hover:border-dashed"
        :class="[title && titleFontSize, title ? 'font-bold' : 'opacity-40']"
        @input="UpdateTitle"
      >
        {{ title }}
      </h1>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyTag')"
        class="group-hover:border-neutral-40 group-hover:focus:border-primary-40 mx-6 flex h-9 flex-col justify-center border-2 border-transparent p-2 text-center text-xs uppercase focus:outline-none group-hover:border-dashed"
        :class="[pdfMode ? 'overflow-visible pb-4' : 'overflow-hidden']"
        @input="UpdateTag"
      >
        {{ tag }}
      </h1>
      <LoadingSpinner v-if="loading" class="text-primary-40 h-6 w-6" aria-hidden="true" />

      <img v-else-if="qrDataImage" :src="qrDataImage" class="mx-auto h-56 w-56" />
      <div v-else class="border-neutral-40 mx-auto flex h-24 w-24 items-center justify-center rounded border-2">QR</div>
      <h1
        contenteditable="true"
        :data-placeholder="t('emptyDescription')"
        class="text-neutral-5 group-hover:border-neutral-40 group-hover:focus:border-primary-40 mx-6 mt-4 flex h-16 flex-col justify-center break-words rounded border-2 border-transparent text-center font-semibold focus:outline-none group-hover:border-dashed"
        :class="[
          description && descriptionFontSize,
          !description && 'opacity-40',
          pdfMode ? 'overflow-visible pb-4' : 'overflow-hidden',
        ]"
        @input="UpdateDescription"
      >
        {{ description }}
      </h1>
      <div v-if="!windowConfig.whiteLabel" class="flex justify-center">
        <PerfitLogo class="h-16 w-16" color="#7B7B7B" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watchEffect, ref, computed, inject } from "vue";
import QRCode from "qrcode";

//Components
import LoadingSpinner from "@atoms/LoadingSpinner.vue";

//Logo
import PerfitLogo from "../PerfitLogo.vue";

//Utils
import { useNotifications } from "@composables/notifications";
import { useI18n } from "vue-i18n";

const { notify } = useNotifications();
const { t } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    tag?: string;
    url: string;
    qrDataImage: string;
  }>(),
  {
    title: "",
    description: "",
    tag: "",
    url: "",
    qrDataImage: "",
  },
);

//Emits
const emit = defineEmits<{
  (e: "update:title", modelValue: string): void;
  (e: "update:description", modelValue: string): void;
  (e: "update:tag", modelValue: string): void;
  (e: "update:qrDataImage", modelValue: string): void;
}>();

const pdfMode = inject("pdfMode", false);
const windowConfig = computed(() => window.config);

const maxTitleLength = 240;
const UpdateTitle = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTitleLength) (event.target as HTMLHeadElement).textContent = props.title;

  emit("update:title", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxDescriptionLength = 120;
const UpdateDescription = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxDescriptionLength) (event.target as HTMLHeadElement).textContent = props.description;
  emit("update:description", (event.target as HTMLHeadElement)?.textContent || "");
};

const maxTagLength = 40;
const UpdateTag = (event: Event) => {
  const text = (event.target as HTMLHeadElement)?.textContent;

  if (text && text.length > maxTagLength) (event.target as HTMLHeadElement).textContent = props.tag;

  emit("update:tag", (event.target as HTMLHeadElement)?.textContent || "");
};

//Refs
const QRElementRef = ref();

//State
const loading = ref(false);

//Functions
const generateQR = async (text) => {
  try {
    loading.value = true;

    const QRImage = await QRCode.toDataURL(text, {
      errorCorrectionLevel: "H",
      scale: 12,
    });

    emit("update:qrDataImage", QRImage);

    loading.value = false;
  } catch (err) {
    loading.value = false;
    notify({
      title: t(`errors.server.title`),
      text: t(`errors.server.text`),
      theme: "error",
      timeout: 0,
    });
  }
};

const descriptionFontSize = computed(() => {
  if (props.description.length >= 100) {
    return "text-xs";
  }
  if (props.description.length >= 80) {
    return "text-sm";
  }
  if (props.description.length >= 60) {
    return "text-base";
  }
  if (props.description.length >= 40) {
    return "text-lg";
  }
  return "text-xl";
});

const titleFontSize = computed(() => {
  if (props.title.length >= 180) {
    return "text-xs";
  }
  if (props.title.length >= 120) {
    return "text-sm";
  }
  if (props.title.length >= 100) {
    return "text-base";
  }
  if (props.title.length >= 80) {
    return "text-lg";
  }
  if (props.title.length >= 60) {
    return "text-xl";
  }
  if (props.title.length >= 40) {
    return "text-2xl";
  }
  return "text-3xl";
});

//Watchers
watchEffect(async () => {
  if (props.url) {
    await generateQR(props.url);
  } else {
    emit("update:qrDataImage", "");
  }
});
</script>

<style>
[contentEditable="true"]:empty:not(:focus):before {
  content: attr(data-placeholder);
}
</style>

<i18n lang="jsonc">
// TODO: i18n
{
  "es": {
    "emptyTitle": "Titulo",
    "emptyDescription": "Descripción",
    "emptyTag": "Etiqueta",
  },
  "pt": {
    "emptyTitle": "Título",
    "emptyDescription": "Descrição",
    "emptyTag": "Etiqueta",
  },
}
</i18n>
